import React, { useState } from 'react';
import { Send } from 'lucide-react';
import { submitToGoHighLevel, sendToZapier } from '../utils/mock';

const CALENDLY_URL = 'https://calendly.com/swallacemeeting/bizdevconsult';

const CalendlyEmbed = () => {
  return (
    <div
      data-testid="calendly-embed"
      className="rounded-2xl overflow-hidden border border-gray-100 bg-white"
      style={{ minWidth: '320px', height: '700px' }}
    >
      <iframe
        src="https://calendly.com/swallacemeeting/bizdevconsult"
        width="100%"
        height="100%"
        frameBorder="0"
        title="Book a strategy call with B2B Rev Co"
        style={{ border: 'none' }}
      />
    </div>
  );
};

const ContactForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await Promise.all([submitToGoHighLevel(formData), sendToZapier(formData)]);
      setIsSubmitted(true);
    } catch (error) {
      console.error('Form submission error:', error);
      alert('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    'w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-gray-900 focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all outline-none';

  return (
    <section id="contact" data-testid="contact-section" className="section" style={{ backgroundColor: 'var(--bg-accent)' }}>
      <div className="container">
        <div className="text-center mb-12">
          <h2 className="heading-lg mb-4">Ready to Build Your Pipeline?</h2>
          <p className="text-base md:text-lg" style={{ color: 'var(--text-secondary)' }}>
            Book a strategy call or send us a message. Either way, we'll show you exactly how we can help.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Calendly Embed */}
          <CalendlyEmbed />

          {/* Toggle to form */}
          <div className="text-center mt-8">
            <button
              onClick={() => setShowForm(!showForm)}
              className="text-sm font-medium underline"
              style={{ color: 'var(--brand-primary)' }}
              data-testid="toggle-form-btn"
            >
              {showForm ? 'Hide form' : 'Prefer to send a message instead?'}
            </button>
          </div>

          {/* Contact Form (expandable) */}
          {showForm && !isSubmitted && (
            <form onSubmit={handleSubmit} className="card max-w-2xl mx-auto mt-8" data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="firstName" className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>First Name *</label>
                  <input id="firstName" name="firstName" type="text" required value={formData.firstName} onChange={handleChange} className={inputClass} data-testid="input-first-name" />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>Last Name *</label>
                  <input id="lastName" name="lastName" type="text" required value={formData.lastName} onChange={handleChange} className={inputClass} data-testid="input-last-name" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>Email *</label>
                  <input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} className={inputClass} data-testid="input-email" />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>Phone</label>
                  <input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} className={inputClass} data-testid="input-phone" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="company" className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>Company *</label>
                  <input id="company" name="company" type="text" required value={formData.company} onChange={handleChange} className={inputClass} data-testid="input-company" />
                </div>
                <div>
                  <label htmlFor="service" className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>Service Interest</label>
                  <select id="service" name="service" value={formData.service} onChange={handleChange} className={inputClass} data-testid="select-service">
                    <option value="">Select a service</option>
                    <option value="Outbound as a Service">Outbound as a Service</option>
                    <option value="Outbound Infrastructure">Outbound Infrastructure</option>
                    <option value="NIMBLE System">NIMBLE Pipeline System</option>
                    <option value="Not sure">Not sure — help me decide</option>
                  </select>
                </div>
              </div>

              <div className="mb-6">
                <label htmlFor="message" className="block text-sm font-medium mb-1.5" style={{ color: 'var(--text-primary)' }}>Tell us about your business</label>
                <textarea
                  id="message"
                  name="message"
                  rows={3}
                  value={formData.message}
                  onChange={handleChange}
                  className={inputClass}
                  placeholder="What's your current revenue? Growth goals? Biggest challenge?"
                  data-testid="input-message"
                />
              </div>

              <button type="submit" disabled={isSubmitting} className="btn-primary w-full py-4" data-testid="contact-submit-btn">
                {isSubmitting ? 'Sending...' : 'Send My Information'}
              </button>

              <p className="text-xs text-center mt-4" style={{ color: 'var(--text-muted)' }}>
                We'll review your info and get back to you within 24 hours.
              </p>
            </form>
          )}

          {/* Success state */}
          {showForm && isSubmitted && (
            <div className="card max-w-2xl mx-auto mt-8 text-center py-12">
              <div className="w-16 h-16 rounded-full mx-auto mb-6 flex items-center justify-center" style={{ backgroundColor: 'var(--brand-success)', color: '#fff' }}>
                <Send className="w-7 h-7" />
              </div>
              <h3 className="heading-md mb-4">Got It. We'll Be in Touch.</h3>
              <p style={{ color: 'var(--text-secondary)' }}>
                Expect to hear from us within 24 hours.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default ContactForm;
